/**
 * Ported from Trello web so a workspace without a logo looks the same here as
 * it does in Trello:
 * trello/web packages/workspace-logo/src/WorkspaceDefaultLogo.tsx
 *
 * The package is private to that monorepo and cannot be depended on, so the
 * algorithm and palette are duplicated. trello-ios carries a third copy in
 * TrelloUIKit/Avatars/AvatarGradient.swift. Keep the order of GRADIENT_NAMES
 * and the palette in step with those, or the same workspace will render a
 * different colour on each platform.
 */
export type WorkspaceGradient = 'teal' | 'green' | 'blue' | 'pink' | 'orange';

const GRADIENT_NAMES: WorkspaceGradient[] = [
  'teal',
  'green',
  'blue',
  'pink',
  'orange',
];

/**
 * Selects a gradient from the sum of the name's char codes.
 *
 * Nothing is stored: the same name always resolves to the same gradient, and
 * renaming a workspace moves it to another one. Which gradient a given name
 * lands on carries no meaning; the point is a stable spread of colours.
 */
export function getWorkspaceGradient(name: string): WorkspaceGradient {
  const sum = name
    .split('')
    .reduce((total, character) => total + character.charCodeAt(0), 0);

  return GRADIENT_NAMES[sum % GRADIENT_NAMES.length];
}

/**
 * Vertical two-stop gradients, matching WorkspaceDefaultLogo.module.less.
 *
 * The names are Trello's and do not describe the colours: "blue" runs purple
 * to navy, and "teal" starts navy.
 *
 * Literal values rather than `token(...)`, for the same reason the initial is
 * a fixed white. Trello references the accent tokens, but `bolder` and
 * `subtle` swap relative luminance between themes: in light mode `bolder` is
 * the deeper colour and the tile runs dark to light, while in dark mode it is
 * the lighter one and the gradient renders upside down. Pinning the light
 * values keeps a workspace the same colour in both themes and keeps the white
 * initial on the darker end.
 *
 * Values are the resolved light-theme tokens, not the legacy hex fallbacks in
 * Trello's stylesheet, which are several revisions behind what ADS now emits.
 */
export const WORKSPACE_GRADIENT_BACKGROUNDS: Record<WorkspaceGradient, string> =
  {
    // color.background.accent.blue.bolder -> .teal.bolder
    teal: 'linear-gradient(#1868DB, #227D9B)',
    // color.background.accent.green.bolder -> .green.subtle
    green: 'linear-gradient(#1F845A, #4BCE97)',
    // color.background.accent.purple.bolder -> .blue.bolder
    blue: 'linear-gradient(#964AC0, #1868DB)',
    // color.background.accent.magenta.bolder -> .magenta.subtle
    pink: 'linear-gradient(#AE4787, #E774BB)',
    // color.background.accent.red.bolder -> .orange.subtle
    orange: 'linear-gradient(#C9372C, #FCA700)',
  };

/** First character of the name, matching `name.substr(0, 1).toUpperCase()`. */
export function getWorkspaceInitial(name: string): string {
  return name.trim().substring(0, 1).toUpperCase();
}
